import Row from 'antd/es/grid/row'
import {
  IdeaHistoryDef,
  PnlSummary,
} from '../../../../../apis/idea/idea-api.def.ts'
import Col from 'antd/es/grid/col'
import IdeyaTypography from '../../../../../common/antd/ideya-typography.tsx'
import { marketDateTimezoneFormatter } from '../../../../../utils/formatters.ts'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'
import { useTranslation } from 'react-i18next'
import { IdeaActionLogTable } from './idea-action-log-table.tsx'
import Alert from 'antd/es/alert/Alert'
import { useLazyClearIdeaAlertsQuery } from '../../../../../apis/idea/idea-api.api.ts'
import { selectUser } from '../../../../../features/user/user.slice.ts'
import { UserInfo } from '../../../../../apis/auth/auth.def.ts'

type AlertType = 'success' | 'info' | 'warning' | 'error'

interface IdeaActionLogsProps {
  actionLog: IdeaHistoryDef
  pnlData: PnlSummary
  refetchData: () => void
}

export const IdeaActionLog: React.FC<IdeaActionLogsProps> = ({
  actionLog,
  pnlData,
  refetchData,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const userInfo: UserInfo = useAppSelector(selectUser)
  const bookId = actionLog.idea.bookId
  const closableAlert = userInfo.bookId == bookId

  const [clearIdeaAlert] = useLazyClearIdeaAlertsQuery()

  const handleClearAlert = () => {
    clearIdeaAlert({ id: actionLog.idea.id })
    refetchData()
  }

  const AlertComponent = () => {
    const latestAlert = [...actionLog.actions]
      .filter((a) => a.showAlert)
      .sort(
        (a, b) =>
          new Date(b.sendingTime).getTime() - new Date(a.sendingTime).getTime()
      )[0]

    if (!latestAlert) return null

    return (
      <Row
        key={latestAlert.ideaActionId}
        style={{ width: '100%', marginBottom: themeStyle.marginSM }}
      >
        <Alert
          closable={closableAlert}
          showIcon
          onClose={handleClearAlert}
          message={
            <IdeyaTypography.Text>
              {latestAlert.alertTitle}: {latestAlert.alertMessage}
            </IdeyaTypography.Text>
          }
          type={latestAlert.alertType?.toLowerCase() as AlertType}
          style={{ width: '100%' }}
        />
      </Row>
    )
  }

  return (
    <>
      <Row>
        <Col span={6}>
          <IdeyaTypography.Text type={'secondary'}>
            {t('idea_list.idea_modal.idea_action_log.direction')}
          </IdeyaTypography.Text>
        </Col>
        <Col span={6}>
          <IdeyaTypography.Text type={'secondary'}>
            {t('idea_list.idea_modal.idea_action_log.date_open')}
          </IdeyaTypography.Text>
        </Col>
        <Col span={6}>
          <IdeyaTypography.Text type={'secondary'}>
            {t('idea_list.idea_modal.idea_action_log.date_closed')}
          </IdeyaTypography.Text>
        </Col>
        <Col span={6}>
          <IdeyaTypography.Text type={'secondary'}>
            {t('idea_list.idea_modal.idea_action_log.user')}
          </IdeyaTypography.Text>
        </Col>
      </Row>
      <Row>
        <Col span={6}>
          <IdeyaTypography.Text>
            {actionLog.idea.direction}
          </IdeyaTypography.Text>
        </Col>
        <Col span={6}>
          <IdeyaTypography.Text>
            {marketDateTimezoneFormatter(
              {
                value: actionLog.idea.createdAt.toString(),
                data: { marketTimeZone: actionLog.idea.marketTimeZone },
              },
              { showTimezone: true }
            )}
          </IdeyaTypography.Text>
        </Col>
        <Col span={6}>
          <IdeyaTypography.Text>
            {actionLog.idea.closedAt
              ? marketDateTimezoneFormatter(
                  {
                    value: actionLog.idea.closedAt.toString(),
                    data: { marketTimeZone: actionLog.idea.marketTimeZone },
                  },
                  { showTimezone: true }
                )
              : '-'}
          </IdeyaTypography.Text>
        </Col>
        <Col span={6}>
          <IdeyaTypography.Text>
            {actionLog.idea.contributor}
          </IdeyaTypography.Text>
        </Col>
      </Row>
      <Row
        style={{
          marginTop: themeStyle.marginSM,
        }}
      >
        <IdeyaTypography.Title
          level={4}
          style={{ marginTop: themeStyle.marginSM }}
        >
          {t('idea_list.idea_modal.idea_action_log.action_log')}
        </IdeyaTypography.Title>
      </Row>
      <AlertComponent />
      <IdeaActionLogTable
        actionLog={actionLog}
        dailyPnlData={pnlData.dailyPnls}
      />
    </>
  )
}
